import { TourAnchor } from "training/anchors";
import {
  hasAnalytics,
  not,
  paymentNotesEnabled,
} from "training/engine/conditions";
import { QUIZ_PATH } from "training/paths";
import { ITourStep } from "training/types";

import { noteTyped } from "./gates";

/** A reload inside the Reports tab restarts at the tab click: the engine opens the hospital view on Estimates. */
const REPORTS_TAB_RESUME = "m6.04-reports-tab";

/** Support's calls, said right after the timeline (review 1: it sits inside the Activity Timeline). */
const SUPPORT_NOTES_TEXT =
  "Clear Health support calls patients who have not paid and leaves a note, which will live here in the Activity Timeline. Always check it before you call someone yourself!";

/**
 * CLE-3015 Module 6: Activity timeline, notes and reporting, then the closing reminder. Text is John's
 * (review 1, 2026-10-23) where he gave wording; the rest is the curriculum's (curriculum-v3, 2026-10-21).
 *
 * Review 1: the support-notes card moved up to right after View message, where the notes it talks about
 * are; Direct Pay (4.10) went back to Module 4; the chat and support cards and the highlighted Cancel
 * Estimate step are gone; the last card is a closing "Reminder!" (cancel your test estimate, how to reach
 * support).
 *
 * Since the Estimate Details redesign (CLE-2961): "Estimate history" is the "Activity Timeline" card, it
 * lists the oldest entry first (OrderView sorts it ascending), and "Add Note" is an icon button labelled
 * "Add note".
 *
 * 6.3 is the tour's one write after Create and Send: a plain note, which changes nothing about the money.
 */
export const MODULE_6: ITourStep[] = [
  {
    id: "m6.01-history",
    module: 6,
    anchor: TourAnchor.ActivityTimeline,
    label: "Activity Timeline",
    mode: "look",
    route: "estimate",
    text: 'This activity timeline will show you everything that has happened with this estimate, oldest first: created and by whom, each text and email sent, "Estimate Opened" when the patient looked at it, payments with who collected them, and more.',
  },
  {
    // Shown when the history has more than three entries.
    id: "m6.01b-show-more",
    module: 6,
    anchor: TourAnchor.ShowMore,
    label: "Show More",
    mode: "click",
    route: "estimate",
    optional: true,
    waitMs: 1000,
    text: 'Click "Show More" to see it all.',
  },
  {
    // On the first sent text or email whose wording we kept (CLE-2576); the trainee may open it.
    id: "m6.02-view-message",
    module: 6,
    anchor: TourAnchor.ViewMessage,
    label: "View message",
    mode: "fill",
    route: "estimate",
    optional: true,
    waitMs: 1500,
    text: "By clicking View message it will show the exact text message the patient received. This is useful if you need to reference it.",
  },
  {
    // Review 1: moved here from the end of the module (it was m6.10).
    id: "m6.02b-support-notes-card",
    module: 6,
    mode: "card",
    route: "estimate",
    when: paymentNotesEnabled,
    text: [
      SUPPORT_NOTES_TEXT,
      'If a patient paid part in cash or by check at the desk, use Add Note and tick "This is a payment note" with the amount; where your hospital has this on, it lowers what the patient owes.',
    ].join("\n"),
  },
  {
    // The 6.3 branch: without payment notes, the card drops its cash-note sentence (it was m6.10n).
    id: "m6.02c-support-notes-card",
    module: 6,
    mode: "card",
    route: "estimate",
    when: not(paymentNotesEnabled),
    text: SUPPORT_NOTES_TEXT,
  },
  {
    id: "m6.03-add-note",
    module: 6,
    anchor: TourAnchor.AddNote,
    mode: "click",
    route: "estimate",
    text: "This button with a pencil and paper will open up a section to log every interaction you had with the patient, such as if the patient called, wants a call back, said they want to pay on the day of service, etc. The note goes in the history with your name and time, and Clear Health support sees it as well. A plain note is safe to save.",
  },
  {
    // Instruction column. The payment-note box shows only where the hospital allows payment notes.
    id: "m6.03b-note-text",
    module: 6,
    anchor: TourAnchor.AddNoteText,
    mode: "fill",
    route: "estimate",
    when: paymentNotesEnabled,
    waitMs: 3000,
    done: noteTyped,
    doneHint: "Type the note to continue.",
    text: 'Type "Clear Certified training", and leave "This is a payment note" unticked.',
  },
  {
    // Instruction column, without the payment-note words (the 6.3 branch).
    id: "m6.03n-note-text",
    module: 6,
    anchor: TourAnchor.AddNoteText,
    mode: "fill",
    route: "estimate",
    when: not(paymentNotesEnabled),
    waitMs: 3000,
    done: noteTyped,
    doneHint: "Type the note to continue.",
    text: 'Type "Clear Certified training".',
  },
  {
    // Instruction column. Add stays disabled until the note has text.
    id: "m6.03c-add",
    module: 6,
    anchor: TourAnchor.AddNoteSubmit,
    label: "Add",
    mode: "click",
    route: "estimate",
    text: 'Click "Add".',
  },
  {
    // 6.4 is two highlights (the tab, then the list inside it), split at a sentence boundary.
    id: "m6.04-reports-tab",
    // First on the hospital view after a navigation.
    waitMs: 8000,
    module: 6,
    anchor: TourAnchor.ReportsTab,
    label: "Reports",
    mode: "click",
    route: "estimates",
    text: "Every Tuesday morning Clear Health publishes last week's payments here, one report per payout. Your business office uses it to post payments.",
  },
  {
    id: "m6.04b-reports-table",
    // Inside the tab the click just opened; the list loads first.
    waitMs: 8000,
    module: 6,
    anchor: TourAnchor.ReportsTable,
    label: "Report Date",
    mode: "look",
    route: "estimates",
    resumeAt: REPORTS_TAB_RESUME,
    text: [
      "This is a report repository of all payouts sent, sorted by date. Filters exist at the top for additional sorting.",
      "Important note! If the list is empty, your hospital has no reports yet, or ask support for access.",
    ].join("\n"),
  },
  {
    id: "m6.05-paid-estimates-download",
    waitMs: 3000,
    module: 6,
    anchor: TourAnchor.PaidEstimatesDownload,
    label: "Estimate Paid Date",
    mode: "look",
    route: "estimates",
    resumeAt: REPORTS_TAB_RESUME,
    when: hasAnalytics,
    text: "If you need to run a custom date range or see who has paid before Tuesday, you can select that date range and download a custom list of paid estimates.",
  },
  {
    // 6.6 points at two tabs, split at a sentence boundary.
    id: "m6.06-providers-tab",
    module: 6,
    anchor: TourAnchor.ProvidersTab,
    label: "Providers",
    mode: "look",
    route: "estimates",
    text: "Providers is the list of facilities and physicians on your estimates.",
  },
  {
    id: "m6.06b-contracts-tab",
    module: 6,
    anchor: TourAnchor.ContractsTab,
    label: "Contracts",
    mode: "look",
    route: "estimates",
    text: "Contracts is where your payer contracts live. Most front-desk users never need either.",
  },
  {
    // The closing reminder (review 1), Module 6's last step. Its button ends the tour: the engine opens the
    // Module 6 quiz (or the first module quiz still owed); `onFinishNavigateTo` is where it goes once all six
    // are passed (a re-run), the knowledge check. Both are pages rather than tour steps. On the hospital view, where the tab steps before it are, so ending the
    // tour does not open the estimate again. No Help Center line: there is none yet (CLE-3033).
    id: "m6.07b-walkthrough-done",
    module: 6,
    title: "Reminder!",
    mode: "card",
    route: "estimates",
    continueLabel: "Take the Module 6 quiz",
    onFinishNavigateTo: QUIZ_PATH,
    text: [
      "Here are a couple of closing items:",
      '• Cancel the test estimate you created in Module 2, so it stops texting you and stays out of your hospital\'s numbers: open it, choose "Cancel Estimate" in the "..." menu, and type "training" in the note.',
      "Clear Health Support Phone: 888-918-2522",
      "Clear Health Support Email: support@clearhealthinc.com",
      "Anytime you are unsure, call or email!",
    ].join("\n"),
  },
];
